import { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { gamesService, type LeaderboardSummary } from '@/services/games.service';

/**
 * GlobalLeaderboardCard — compact "you vs the world" widget shown on the
 * Book Detail modal's Resources tab beneath the Cosmic Typing CTA.
 *
 * Displays the global #1 best score for the (book, game) and, when a
 * reader is selected, the same reader's own best alongside it. Re-fetches
 * whenever `refreshKey` changes — the parent bumps it after a successful
 * score submission so the widget reflects the new high without a manual
 * reload.
 *
 * Edge cases:
 *   - Nobody has played yet → "Be the first" empty state.
 *   - Reader hasn't played → only the leader row is shown, with a
 *     "You haven't played yet" prompt below it.
 *   - Reader IS #1 → single row with a celebratory note instead of two
 *     rows that would say the same thing.
 */
export interface GlobalLeaderboardCardProps {
  bookId: string;
  /** Optional — when provided, the reader's own best is fetched + shown. */
  readerId?: string;
  /** Display name of the active reader; used in row labels. */
  readerName?: string;
  /** Avatar slug of the active reader. */
  readerAvatarSlug?: string;
  /** Bumping this triggers a re-fetch — used after onGameOver. */
  refreshKey: number;
}

export function GlobalLeaderboardCard({
  bookId,
  readerId,
  readerName,
  readerAvatarSlug,
  refreshKey,
}: GlobalLeaderboardCardProps) {
  const [summary, setSummary] = useState<LeaderboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrored(false);

    gamesService
      .summary({ bookId, readerId })
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // Quiet failure — the widget is supplementary; we don't shout at
        // the user for a leaderboard miss.
        console.warn('Leaderboard summary fetch failed:', err);
        setErrored(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bookId, readerId, refreshKey]);

  if (errored) return null;

  // The reader is #1 when both rows refer to the same reader id. Comparing
  // by id (not by reference) is correct because the server returns two
  // separate-but-equal objects in that case.
  const readerIsTop = Boolean(
    summary?.top && summary?.reader && summary.top.readerId === summary.reader.readerId,
  );

  return (
    <section
      aria-labelledby="global-leaderboard-heading"
      className="mb-4 overflow-hidden rounded-2xl border border-or-slate-200 bg-white shadow-or-card"
    >
      <header className="flex items-center gap-2 border-b border-or-slate-100 bg-gradient-to-r from-or-orange-50 to-or-cream-50 px-4 py-3">
        <Trophy className="h-4 w-4 text-or-orange-600" aria-hidden />
        <h3
          id="global-leaderboard-heading"
          className="font-display text-[14px] font-bold text-or-slate-900"
        >
          Cosmic Typing Global Leaderboard
        </h3>
      </header>

      <div className="px-4 py-3">
        {loading ? (
          <LoadingSkeleton />
        ) : !summary?.top ? (
          <EmptyState />
        ) : readerIsTop && summary.reader ? (
          <SingleRow
            entry={summary.reader}
            readerName={readerName ?? summary.reader.readerName}
            readerAvatarSlug={readerAvatarSlug ?? summary.reader.readerAvatarSlug}
          />
        ) : (
          <CompareRows
            top={summary.top}
            reader={summary.reader}
            readerName={readerName}
            readerAvatarSlug={readerAvatarSlug}
            // No reader picked at all → don't render the placeholder
            // "you haven't played" row; just the leader is enough.
            showReaderPlaceholder={Boolean(readerId)}
          />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

function CompareRows({
  top,
  reader,
  readerName,
  readerAvatarSlug,
  showReaderPlaceholder,
}: {
  top: NonNullable<LeaderboardSummary['top']>;
  reader: LeaderboardSummary['reader'];
  readerName: string | undefined;
  readerAvatarSlug: string | undefined;
  showReaderPlaceholder: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Row
        rankLabel="#1"
        rankTone="gold"
        name={top.readerName}
        avatarSlug={top.readerAvatarSlug}
        score={top.bestScore}
      />
      {reader ? (
        <Row
          rankLabel={`#${reader.rank}`}
          rankTone="self"
          // Prefer the locally-stored display name (fresher on rename
          // races) but fall back to the server projection.
          name={readerName ?? reader.readerName}
          avatarSlug={readerAvatarSlug ?? reader.readerAvatarSlug}
          score={reader.bestScore}
          suffix="(you)"
        />
      ) : showReaderPlaceholder ? (
        <div className="rounded-xl border border-dashed border-or-slate-200 px-3 py-2 text-[12px] text-or-slate-500">
          You haven't played yet — set a score to claim your spot.
        </div>
      ) : null}
    </div>
  );
}

function SingleRow({
  entry,
  readerName,
  readerAvatarSlug,
}: {
  entry: NonNullable<LeaderboardSummary['top']>;
  readerName: string;
  readerAvatarSlug: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Row
        rankLabel="#1"
        rankTone="gold"
        name={readerName}
        avatarSlug={readerAvatarSlug}
        score={entry.bestScore}
        suffix="(you)"
      />
      <p className="px-1 text-[12px] font-semibold text-or-orange-700">
        You're at the top — beat your own score!
      </p>
    </div>
  );
}

function Row({
  rankLabel,
  rankTone,
  name,
  avatarSlug,
  score,
  suffix,
}: {
  rankLabel: string;
  rankTone: 'gold' | 'self';
  name: string;
  avatarSlug: string;
  score: number;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-or-cream-50/60 px-3 py-2">
      <span
        className={
          rankTone === 'gold'
            ? 'flex h-7 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 text-[11px] font-bold text-white shadow-or-card'
            : 'flex h-7 w-10 shrink-0 items-center justify-center rounded-full border border-or-slate-200 bg-white text-[11px] font-bold text-or-slate-700'
        }
        aria-label={`Rank ${rankLabel}`}
      >
        {rankLabel}
      </span>
      <ReaderAvatar name={name} avatarSlug={avatarSlug} size="sm" />
      <div className="flex-1 truncate text-[13px] font-semibold text-or-slate-900">
        {name}
        {suffix ? <span className="ml-1 text-[12px] text-or-slate-500">{suffix}</span> : null}
      </div>
      <div
        className="font-display text-[18px] font-bold tabular-nums text-or-slate-900"
        aria-label={`Best score ${score.toLocaleString()}`}
      >
        {score.toLocaleString()}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-or-slate-200 px-3 py-4 text-center text-[12px] text-or-slate-500">
      <span className="font-semibold text-or-slate-700">No scores yet.</span> Be the first to set a
      high score for this book!
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl bg-or-cream-50/60 px-3 py-2">
          <div className="h-7 w-10 animate-pulse rounded-full bg-or-slate-200" />
          <div className="h-8 w-8 animate-pulse rounded-full bg-or-slate-200" />
          <div className="h-3 flex-1 animate-pulse rounded-full bg-or-slate-200" />
          <div className="h-5 w-12 animate-pulse rounded-full bg-or-slate-200" />
        </div>
      ))}
    </div>
  );
}
